import { Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { SessionProvider } from "@/contexts/SessionContext";
import { PersonaProvider } from "@/contexts/PersonaContext";
import { PlatformSettingsProvider } from "@/contexts/PlatformSettingsContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { ThemeProvider } from "@/components/providers/ThemeProvider";

import { ThemeColorSync } from "@/components/providers/ThemeColorSync";
import { AppErrorBoundary } from "@/components/providers/AppErrorBoundary";
import { LazyToastHost } from "@/components/providers/LazyToastHost";
import { OneSignalProvider } from "@/integrations/onesignal/OneSignalProvider";
import { routes } from "@/lib/routes";

const queryClient = new QueryClient();

// Create router lazily to avoid module-scope evaluation blocking boot
let _router: ReturnType<typeof createBrowserRouter> | null = null;
function getRouter() {
  if (!_router) _router = createBrowserRouter(routes);
  return _router;
}

const App = () => (
  <AppErrorBoundary>
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem enableColorScheme={false}>
        <ThemeColorSync />
          <QueryClientProvider client={queryClient}>
            <SessionProvider>
              <PlatformSettingsProvider>
                <PersonaProvider>
                <CookieConsentProvider>
                  <OneSignalProvider>
                    <LazyToastHost />
                    <Suspense fallback={null}>
                      <RouterProvider router={getRouter()} />
                    </Suspense>
                  </OneSignalProvider>
                </CookieConsentProvider>
                </PersonaProvider>
              </PlatformSettingsProvider>
            </SessionProvider>
          </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  </AppErrorBoundary>
);

export default App;
